/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { tap, catchError, throwError, from, map, shareReplay, take, switchMap, Observable } from 'rxjs';
import { IamConfigService } from '@msi/commandcentral-user-authentication';

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'set-cookie'];

@Injectable({ providedIn: 'root' })
export class IdmUrlCacheService {
    readonly idmUrl$: Observable<string | null> = from(this.iamConfigService.getIamConfig()).pipe(
        map((config) => config?.idmUrl ?? null),
        shareReplay(1)
    );

    constructor(private iamConfigService: IamConfigService) {}
}

function logClientError(req: HttpRequest<unknown>, error: HttpErrorResponse): void {
    try {
        console.error(`[IDM] Request Data:`, JSON.stringify({
            method: req.method,
            url: req.url,
            headers: req.headers?.keys()
                .reduce((acc, key) => ({
                    ...acc,
                    [key]: SENSITIVE_HEADERS.includes(key.toLowerCase()) ? '***' : req.headers.getAll(key)
                }), {}),
            body: req.body ?? null,
            params: req.params?.toString() ?? '',
            urlWithParams: req.urlWithParams,
            responseType: req.responseType,
            withCredentials: req.withCredentials
        }, null, 2));
        console.error(`[IDM] Response Data:`, JSON.stringify({
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? null,
            headers: error.headers?.keys().reduce((acc, key) => ({ ...acc, [key]: error.headers.getAll(key) }), {}) ?? {},
            body: error.error ?? null
        }, null, 2));
    } catch (e) {
        console.error(`[IDM] Failed to serialize request/response data:`, e?.toString());
    }
}

function handleIdmRequest(req: HttpRequest<unknown>, next: HttpHandlerFn) {
    const startTime = Date.now();
    console.info(`[IDM] Request: ${req.method} ${req.url}`);

    return next(req).pipe(
        tap((event) => {
            if (event instanceof HttpResponse) {
                const elapsed = Date.now() - startTime;
                console.info(`[IDM] Response: ${event.status} ${req.method} ${req.url} (${elapsed}ms)`);
            }
        }),
        catchError((error) => {
            const elapsed = Date.now() - startTime;
            if (error.name === 'TimeoutError') {
                console.error(`[IDM] Timeout: ${req.method} ${req.url} after ${elapsed}ms`);
            } else if (error instanceof HttpErrorResponse) {
                console.error(`[IDM] Error: ${error.status} ${req.method} ${req.url} (${elapsed}ms) ${error.message}`);
                if (error.status >= 400 && error.status < 500) {
                    logClientError(req, error);
                }
            } else {
                console.error(`[IDM] Error: ${req.method} ${req.url} (${elapsed}ms) ${error?.toString()}`);
            }
            return throwError(() => error);
        })
    );
}

export const idmLoggingInterceptor: HttpInterceptorFn = (req, next) => {
    const idmUrlCacheService = inject(IdmUrlCacheService);

    return idmUrlCacheService.idmUrl$.pipe(
        take(1),
        switchMap((idmUrl) => {
            if (!idmUrl || !req.url.includes(idmUrl)) {
                return next(req);
            }
            return handleIdmRequest(req, next);
        })
    );
};
